/**
 * Tooltip Component
 * Based on Radix UI Tooltip primitive with a convenient wrapper API
 *
 * Supports two usage patterns:
 *
 * 1. Simple wrapper (recommended for most cases):
 *    <Tooltip content="Tooltip text" position="right">
 *      <button>Hover me</button>
 *    </Tooltip>
 *
 * 2. Radix primitive pattern (for advanced usage):
 *    <TooltipProvider>
 *      <Tooltip>
 *        <TooltipTrigger asChild><button>Hover</button></TooltipTrigger>
 *        <TooltipContent>Content</TooltipContent>
 *      </Tooltip>
 *    </TooltipProvider>
 */

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

// Radix primitives
const TooltipProvider = TooltipPrimitive.Provider;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className = '', sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={`z-50 overflow-hidden rounded-md border bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ${className}`}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Simple Tooltip wrapper props
export interface TooltipProps {
  content?: React.ReactNode;
  children: React.ReactNode;
  position?: 'top' | 'right' | 'bottom' | 'left';
  delay?: number;
  className?: string;
  // Radix props for compatibility
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  delayDuration?: number;
  disableHoverableContent?: boolean;
}

/**
 * Unified Tooltip component that supports both simple and Radix usage patterns
 *
 * Simple usage: <Tooltip content="Text">{trigger}</Tooltip>
 * Radix usage: <Tooltip><TooltipTrigger>...</TooltipTrigger><TooltipContent>...</TooltipContent></Tooltip>
 *
 * NOTE: When using many tooltips in a list, wrap the parent component with
 * <TooltipProvider> for better performance. The simple usage pattern will
 * still work without it, but performance is better with a shared provider.
 */
export function Tooltip({
  content,
  children,
  position = 'top',
  delay = 200,
  className,
  // Radix props
  open,
  defaultOpen,
  onOpenChange,
  delayDuration,
  disableHoverableContent,
}: TooltipProps) {
  // If content prop is provided, use simple wrapper pattern
  // Note: This doesn't create its own Provider - parent should wrap with TooltipProvider
  // for optimal performance when rendering many tooltips
  if (content !== undefined) {
    return (
      <TooltipPrimitive.Root delayDuration={delay}>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent side={position} className={className}>
          {content}
        </TooltipContent>
      </TooltipPrimitive.Root>
    );
  }

  // Otherwise, use as Radix Root (for advanced usage with TooltipTrigger + TooltipContent)
  return (
    <TooltipPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delayDuration={delayDuration}
      disableHoverableContent={disableHoverableContent}
    >
      {children}
    </TooltipPrimitive.Root>
  );
}

// Export Radix primitives for advanced usage
export { TooltipTrigger, TooltipContent, TooltipProvider };
